import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import type { ViewMode } from "@/components/itinerary/ItineraryPreview";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { CoverPhotoBackground } from "@/components/itinerary/CoverPhotoBackground";
import { TripCoverPhoto } from "@/hooks/useUnsplash";
import { supabase } from "@/integrations/supabase/client";
import { Trip, TripData } from "@/hooks/useTrips";
import { safeFormat } from "@/lib/formatDateTime";
import { ItineraryCardsView } from "@/components/itinerary/ItineraryCardsView";
import { Button } from "@/components/ui/button";
import { Calendar, LayoutList } from "lucide-react";

interface SharedTripData {
  trip_id: string;
  display_name: string;
  destination: string | null;
  start_date: string | null;
  end_date: string | null;
  trip_data: TripData | null;
  cover_photo_id: string | null;
  cover_photo_url: string | null;
  cover_photo_blur_hash: string | null;
  cover_photo_alt_description: string | null;
  cover_photo_photographer_name: string | null;
  cover_photo_photographer_username: string | null;
  cover_photo_position_y: number | null;
}

function useSharedTrip(token: string | undefined) {
  return useQuery({
    queryKey: ["shared-trip", token],
    queryFn: async () => {
      if (!token) return null;

      const { data, error } = await supabase.rpc("get_trip_by_share_token", {
        token,
      });

      if (error) throw error;
      if (!data || data.length === 0) return null;

      return data[0] as SharedTripData;
    },
    enabled: !!token,
  });
}

function extractCoverPhotoFromShared(data: SharedTripData): TripCoverPhoto | null {
  if (!data.cover_photo_id || !data.cover_photo_url) {
    return null;
  }
  return {
    id: data.cover_photo_id,
    url: data.cover_photo_url,
    blurHash: data.cover_photo_blur_hash || null,
    altDescription: data.cover_photo_alt_description || null,
    photographerName: data.cover_photo_photographer_name || "Unknown",
    photographerUsername: data.cover_photo_photographer_username || "unsplash",
    positionY: data.cover_photo_position_y ?? 50,
  };
}

function sharedDataToTrip(data: SharedTripData): Trip {
  return {
    id: data.trip_id,
    user_id: "",
    tripit_id: null,
    display_name: data.display_name,
    start_date: data.start_date,
    end_date: data.end_date,
    destination: data.destination,
    trip_data: data.trip_data,
    email_source: null,
    last_synced_at: "",
    created_at: "",
    cover_photo_id: data.cover_photo_id,
    cover_photo_url: data.cover_photo_url,
    cover_photo_blur_hash: data.cover_photo_blur_hash,
    cover_photo_alt_description: data.cover_photo_alt_description,
    cover_photo_photographer_name: data.cover_photo_photographer_name,
    cover_photo_photographer_username: data.cover_photo_photographer_username,
    cover_photo_position_y: data.cover_photo_position_y,
  };
}

export default function SharedItinerary() {
  const { token } = useParams<{ token: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: sharedTrip, isLoading, error } = useSharedTrip(token);

  // Get view mode from URL or default to 'by-type'
  const urlViewMode = searchParams.get("view") as ViewMode | null;
  const [viewMode, setViewMode] = useState<ViewMode>(
    urlViewMode === "by-date" ? "by-date" : "by-type"
  );

  // Update URL when view mode changes
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode === "by-date") {
      setSearchParams({ view: "by-date" });
    } else {
      searchParams.delete("view");
      setSearchParams(searchParams);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
      </div>
    );
  }

  if (error || !sharedTrip) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-display text-2xl font-bold text-foreground">
              Itinerary Not Found
            </h1>
            <p className="mt-2 text-muted-foreground">
              This link may have expired or is invalid.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const trip = sharedDataToTrip(sharedTrip);
  const coverPhoto = extractCoverPhotoFromShared(sharedTrip);

  return (
    <div className="flex min-h-screen flex-col bg-muted/30">
      <Header />

      <main className="flex-1 py-8">
        <div className="container">
          <div className="mb-6">
            <h1 className="font-display text-2xl font-bold text-foreground">
              {sharedTrip.display_name}
            </h1>
          </div>

          {/* Cover photo with card overlay layout */}
          <div>
            {/* Cover photo - rounded corners at top, with destination/date overlay */}
            <CoverPhotoBackground
              coverPhoto={coverPhoto}
              destination={sharedTrip.destination}
              dateRange={
                sharedTrip.start_date && sharedTrip.end_date
                  ? `${safeFormat(sharedTrip.start_date, "MMM d, yyyy")} – ${safeFormat(sharedTrip.end_date, "MMM d, yyyy")}`
                  : sharedTrip.start_date
                    ? safeFormat(sharedTrip.start_date, "MMM d, yyyy")
                    : null
              }
            />

            {/* Card overlaps into photo - large negative margin to overcome padding */}
            <div className="relative z-10" style={{ marginTop: '-10rem' }}>
              <div className="bg-card shadow-2xl">
                <ErrorBoundary>
                  <div className="p-4 sm:p-6 md:p-12 max-w-4xl mx-auto">
                    <div className="flex justify-end mb-4 mt-4">
                      <div className="flex w-full sm:w-auto rounded-md border border-border overflow-hidden">
                        <Button
                          variant={viewMode === 'by-type' ? 'default' : 'ghost'}
                          size="sm"
                          className="rounded-none flex-1 sm:flex-none"
                          onClick={() => handleViewModeChange('by-type')}
                        >
                          <LayoutList className="h-4 w-4 mr-2" />
                          By Type
                        </Button>
                        <Button
                          variant={viewMode === 'by-date' ? 'default' : 'ghost'}
                          size="sm"
                          className="rounded-none flex-1 sm:flex-none"
                          onClick={() => handleViewModeChange('by-date')}
                        >
                          <Calendar className="h-4 w-4 mr-2" />
                          By Date
                        </Button>
                      </div>
                    </div>

                    <ItineraryCardsView
                      tripData={trip.trip_data as TripData | null}
                      viewMode={viewMode}
                    />
                  </div>
                </ErrorBoundary>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
